import React, { useEffect, useState } from 'react';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IListingData } from '@models/listing';
import { headers } from './content';
import { useLazyQuery } from '@apollo/client';
import { businessPartnerQuery, getPolicyBills } from '../../../queries';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import Loader from '@components/loader/Loader';
import { mapToListingData } from './utils';
import PaymentTermsPopup from '../change-payment-terms/PaymentTermsPopup';
import { IAbstractRecord } from '@models';
import ListingTable from '@components/table/ListingTable';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import { enumListAsRecordObject } from '@utils/graph-utils';
import { IEnhanceTableHeaderClickable } from '@components/table/widgets/shared-with-old';
import ViewBreakdownShell from '../breakdown-popup/ViewBreakdownShell';
import PrecisionService from '@services/PrecisionService';
import { IPaymentTermsPopups } from '..';
import { cloneDeep } from 'lodash';

interface IPaymentTermsSchedule {
  policyId: string;
  policyNumber: string;
  isGroup?: boolean;
}

const PaymentTermsSchedule: React.FC<IPaymentTermsSchedule> = ({
  policyId,
  policyNumber,
  isGroup,
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentTermsPopup, setPaymentTermsPopup] =
    useState<IPaymentTermsPopups>({
      isOpen: false,
      type: 'changePaymentTerms',
      generalData: {
        bill: {
          id: '',
          type: '',
        },
        policy: {
          id: policyId,
          number: policyNumber,
        },
        amendment: {
          id: '',
          number: '',
          effectiveDate: '',
        },
      },
    });
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 5,
    pageNumber: 0,
    totalCount: 0,
  });

  const tableAction: IEnhancedMenuItem[] = [
    {
      name: 'changepaymentterms',
      title: 'Change Payment Terms',
      target: ActionTarget.Entity,
      displayActionPerRecord: true,
      onClick: (payload: string[]) => {
        const currentBill = tableData.pagedItems[payload[0]]; // since only 1 record is selected

        setPaymentTermsPopup((oldState) => {
          const newState = cloneDeep(oldState);
          newState.type = 'changePaymentTerms';
          newState.isOpen = true;
          newState.generalData.bill = {
            id: currentBill.id,
            type: currentBill.billType?.toLowerCase(),
          };
          newState.generalData.amendment = {
            id: currentBill.linkedAmendmentId,
            number: currentBill.linkedAmendment,
            effectiveDate: currentBill.linkedAmendmentEffectiveDate,
          };
          return newState;
        });
      },
    },
  ];

  const [getPolicyBillsLazy] = useLazyQuery(getPolicyBills);
  const [getBusinessPartnerDetailsLazy] = useLazyQuery(businessPartnerQuery);

  const loadData = async () => {
    setLoading(true);

    try {
      const { data, error } = await getPolicyBillsLazy({
        variables: {
          policyId: policyId,
        },
      });

      if (error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }

      const categories = enumListAsRecordObject(
        data?.Accounting_ParentBillCategoryList?.enumValues
      );

      const newTableData = mapToListingData(
        data?.Accounting?.queries?.GetParentBillDetails,
        categories
      );
      setTableData({ ...newTableData });
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoading(false);
      setBooted(true);
    }
  };

  const initialize = async () => {
    await loadData();
  };

  useEffect(() => {
    initialize();
  }, []);

  async function handleTooltipFetch(id: string): Promise<IAbstractRecord> {
    const popoverRows: IAbstractRecord = {};
    await getBusinessPartnerDetailsLazy({
      variables: {
        parentBillID: id,
      },
    }).then((response) => {
      response?.data?.Accounting?.queries?.GetBusinessPartnersCommissions?.forEach(
        (obj: IAbstractRecord) => {
          popoverRows[obj.businessPartner_Id] = {
            BusinessPartner: obj.businessPartner_FullName,
            Share: PrecisionService.multiplyBy100(
              Number(obj.accounting_Bills_CommissionPercentage)
            ),
            Commission: obj.accounting_Bills_TotalCommission,
            Currency: obj.accounting_Bills_Currency?.Symbol,
          };
        }
      );
    });
    return popoverRows;
  }

  function viewBreakdownCallback(payload: IAbstractRecord) {
    setPaymentTermsPopup((oldState) => {
      const newState = cloneDeep(oldState);
      newState.isOpen = true;
      newState.type = 'viewBreakdown';
      newState.generalData.amendment.id = payload.columns.linkedAmendmentId;
      newState.generalData.amendment.number = payload.columns.linkedAmendment;
      newState.generalData.bill.id = payload.columns.id;
      newState.generalData.bill.type = payload.columns.billType?.toLowerCase();
      return newState;
    });
  }

  headers.commission.tooltipSettings.tooltipFetch = handleTooltipFetch;

  (headers.viewBreakdown as IEnhanceTableHeaderClickable).callback =
    viewBreakdownCallback;

  if (!booted) {
    return <Loader />;
  }

  return (
    <>
      <ListingTable
        inlineTitle={
          isGroup ? 'Group Payment Terms Schedule' : 'Payment Terms Schedule'
        }
        name="paymentTermsSchedule"
        data={tableData}
        headers={headers}
        loader={loading}
        actions={tableAction}
        inline
        orderByAscendingByDefault
      />
      {paymentTermsPopup.isOpen &&
        (paymentTermsPopup.type === 'changePaymentTerms' ? (
          <PaymentTermsPopup
            generalData={paymentTermsPopup.generalData}
            currencySymbol={Object.values(tableData.pagedItems)[0]?.Currency}
            open={paymentTermsPopup.isOpen}
            onClose={() => {
              setPaymentTermsPopup((oldState) => ({
                ...oldState,
                isOpen: false,
              }));
              initialize();
            }}
          />
        ) : (
          <ViewBreakdownShell
            generalData={paymentTermsPopup.generalData}
            open={paymentTermsPopup.isOpen}
            onClose={() => {
              setPaymentTermsPopup((oldState) => ({
                ...oldState,
                isOpen: false,
              }));
              initialize();
            }}
          />
        ))}
    </>
  );
};

export default PaymentTermsSchedule;
